import { LoadingButton } from '@mui/lab';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete,
  Chip,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Transition } from '../../components/Transition';
import { NumericFormatCustom } from '../../components/NumericCustomFormatter';
import { ADVERTISE_PACKAGE_TYPE } from '../../constants';
import { useEffect, useState } from 'react';
import { CompaniesList } from '../../client/services';
import { getBrokersList } from '../../services/brokers-list';

interface IProps {
  toggle: boolean;
  onClose: () => void;
  values: any;
  handleSubmit: () => void;
  setFieldValue: (key: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<any>) => void;
  errors: any;
  touched: any;
  isSubmitting: boolean;
  resetForm: () => void;
}

const AdvertisePackageFormModal: React.FC<IProps> = ({
  toggle,
  onClose,
  errors,
  touched,
  values,
  isSubmitting,
  setFieldValue,
  handleChange,
  handleSubmit,
  resetForm,
}) => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [brokers, setBrokers] = useState<any[]>([]);
  const [isLoadingBrokers, setIsLoadingBrokers] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);

  // Fetch companies when modal opens and type is 'company'
  useEffect(() => {
    if (toggle && values.type === 'company') {
      fetchCompanies();
    }
    // Fetch brokers when modal opens and type is 'broker' only (not individual_broker)
    // Individual + Broker packages are available to ALL brokers, no selection needed
    if (toggle && values.type === 'broker') {
      fetchBrokers();
    }
    // Reset initial mount flag when modal closes
    if (!toggle) {
      setIsInitialMount(true);
    }
  }, [toggle, values.type]);

  const fetchCompanies = async (search = '') => {
    try {
      setIsLoadingCompanies(true);
      const response = await CompaniesList.find({
        query: {
          limit: 100,
          ...(search && { search: search }),
        },
      });
      if (response?.data) {
        setCompanies(response.data);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const searchCompanies = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const searchValue = e.target.value;
    fetchCompanies(searchValue);
  };

  const onScrollFetchCompanies = async (
    _e: React.UIEvent<HTMLUListElement, UIEvent>,
    _search: string
  ) => {
    // Implement pagination if needed
  };

  const fetchBrokers = async (search = '') => {
    try {
      setIsLoadingBrokers(true);
      const response = await getBrokersList({
        limit: 100,
        ...(search && { search: search }),
      });
      if (response?.data) {
        setBrokers(response.data);
      }
    } catch (error) {
      console.error('Error fetching brokers:', error);
    } finally {
      setIsLoadingBrokers(false);
    }
  };

  const searchBrokers = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const searchValue = e.target.value;
    fetchBrokers(searchValue);
  };

  const onScrollFetchBrokers = async (
    _e: React.UIEvent<HTMLUListElement, UIEvent>,
    _search: string
  ) => {
    // Implement pagination if needed
  };

  const isIndividual = values.type === 'individual';
  const isCompany = values.type === 'company';
  const isBroker = values.type === 'broker';
  const isIndividualBroker = values.type === 'individual_broker';

  // Reset company-related fields when type changes (only for new packages)
  useEffect(() => {
    // Skip on initial mount if editing (has id) - preserve existing values
    if (isInitialMount && values.id) {
      setIsInitialMount(false);
      return;
    }
    setIsInitialMount(false);
    
    // Only reset fields if this is a new package (no id)
    const isNewPackage = !values.id;
    const isIndividualType = values.type === 'individual';
    const isCompanyType = values.type === 'company';
    
    if (isIndividualType) {
      setFieldValue('packageType', 'duration_based');
      if (isNewPackage) {
        setFieldValue('creditCount', '');
        setFieldValue('creditExpirationDays', '');
        setFieldValue('companyId', null);
        setFieldValue('companyIds', []);
        setFieldValue('companySelection', '');
        setFieldValue('brokerId', null);
        setFieldValue('brokerIds', []);
        setFieldValue('brokerSelection', '');
      }
    } else if (isCompanyType) {
      setFieldValue('packageType', 'credit_based');
      // Only clear duration if it's a new package AND duration is not already set
      // This preserves duration when editing existing packages
      if (isNewPackage && !values.duration) {
        setFieldValue('duration', '');
      }
      if (!values.companySelection) {
        setFieldValue('companySelection', 'all');
        setFieldValue('companyId', null);
        setFieldValue('companyIds', []);
      }
      if (isNewPackage) {
        setFieldValue('brokerId', null);
        setFieldValue('brokerIds', []);
        setFieldValue('brokerSelection', '');
      }
    } else if (values.type === 'broker') {
      setFieldValue('packageType', 'duration_based');
      if (isNewPackage) {
        setFieldValue('creditCount', '');
        setFieldValue('creditExpirationDays', '');
        setFieldValue('companyId', null);
        setFieldValue('companyIds', []);
        setFieldValue('companySelection', '');
      }
      if (!values.brokerSelection) {
        setFieldValue('brokerSelection', 'all');
        setFieldValue('brokerId', null);
        setFieldValue('brokerIds', []);
      }
    } else if (values.type === 'individual_broker') {
      setFieldValue('packageType', 'duration_based');
      if (isNewPackage) {
        setFieldValue('creditCount', '');
        setFieldValue('creditExpirationDays', '');
        setFieldValue('companyId', null);
        setFieldValue('companyIds', []);
        setFieldValue('companySelection', '');
        // Individual + Broker packages are available to ALL - no specific broker selection
        setFieldValue('brokerId', null);
        setFieldValue('brokerIds', []);
        setFieldValue('brokerSelection', '');
      }
    }
  }, [values.type, values.id, values.duration, values.companySelection, values.brokerSelection, isInitialMount, setFieldValue]);

  // Handle company selection change
  const handleCompanySelectionChange = (value: string) => {
    setFieldValue('companySelection', value);
    if (value === 'all') {
      setFieldValue('companyId', null);
      setFieldValue('companyIds', []);
    } else {
      // Keep current companyIds if already selected
      if (!values.companyIds) {
        setFieldValue('companyIds', []);
      }
    }
  };

  // Handle broker selection change
  const handleBrokerSelectionChange = (value: string) => {
    setFieldValue('brokerSelection', value);
    if (value === 'all') {
      setFieldValue('brokerId', null);
      setFieldValue('brokerIds', []);
    } else {
      // Keep current brokerIds if already selected
      if (!values.brokerIds) {
        setFieldValue('brokerIds', []);
      }
    }
  };

  return (
    <Dialog
      fullWidth
      open={toggle}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => {
        resetForm();
        onClose();
      }}
      aria-describedby='alert-dialog-slide-description'
    >
      <DialogTitle sx={{ mt: -0.5, mb: -0.5 }}>Advertise Package</DialogTitle>
      <form
        onSubmit={handleSubmit}
        autoComplete='off'
      >
        <DialogContent dividers>
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                fullWidth
                label={'Label En'}
                name='labelEn'
                id='labelEn'
                onChange={handleChange}
                value={values.labelEn}
                error={Boolean(errors.labelEn && touched.labelEn)}
                helperText={
                  Boolean(errors.labelEn && touched.labelEn) && errors?.labelEn
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                fullWidth
                label={'Label Ar'}
                name='labelAr'
                id='labelAr'
                onChange={handleChange}
                value={values.labelAr}
                error={Boolean(errors.labelAr && touched.labelAr)}
                helperText={
                  Boolean(errors.labelAr && touched.labelAr) && errors?.labelAr
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                fullWidth
                label={'Code'}
                name='code'
                id='code'
                onChange={handleChange}
                value={values.code}
                error={Boolean(errors.code && touched.code)}
                helperText={
                  Boolean(errors.code && touched.code) && errors?.code
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                fullWidth
                label={'Price'}
                name='price'
                id='price'
                onChange={handleChange}
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                }}
                value={values.price}
                error={Boolean(errors.price && touched.price)}
                helperText={
                  Boolean(errors.price && touched.price) && errors?.price
                }
              />
            </Grid>

            {/* Duration field - only for Individual, Broker, and Individual + Broker */}
            {(isIndividual || isBroker || isIndividualBroker) && (
              <Grid
                item
                xs={12}
                sm={12}
              >
                <TextField
                  fullWidth
                  label={'Duration (Days)'}
                  name='duration'
                  id='duration'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.duration}
                  error={Boolean(errors.duration && touched.duration)}
                  helperText={
                    Boolean(errors.duration && touched.duration) &&
                    errors?.duration
                  }
                />
              </Grid>
            )}

            {/* Credit fields - only for Company */}
            {isCompany && (
              <>
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <TextField
                    fullWidth
                    label={'Credit Count'}
                    name='creditCount'
                    id='creditCount'
                    onChange={handleChange}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    value={values.creditCount || ''}
                    error={Boolean(errors.creditCount && touched.creditCount)}
                    helperText={
                      Boolean(errors.creditCount && touched.creditCount) &&
                      errors?.creditCount
                    }
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <TextField
                    fullWidth
                    label={'Credit Expiration Days'}
                    name='creditExpirationDays'
                    id='creditExpirationDays'
                    onChange={handleChange}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    value={values.creditExpirationDays || ''}
                    error={Boolean(
                      errors.creditExpirationDays &&
                        touched.creditExpirationDays
                    )}
                    helperText={
                      Boolean(
                        errors.creditExpirationDays &&
                          touched.creditExpirationDays
                      ) && errors?.creditExpirationDays
                    }
                  />
                </Grid>

                {/* Duration field for Company (ad publishing duration) */}
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <TextField
                    fullWidth
                    label={'Ad Duration (Days)'}
                    name='duration'
                    id='duration'
                    onChange={handleChange}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    value={values.duration || ''}
                    error={Boolean(errors.duration && touched.duration)}
                    helperText={
                      Boolean(errors.duration && touched.duration) &&
                      errors?.duration
                    }
                  />
                </Grid>

                {/* Company Selection */}
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <FormControl
                    fullWidth
                    error={Boolean(
                      touched?.companySelection && errors?.companySelection
                    )}
                  >
                    <InputLabel>{'Company Selection'}</InputLabel>
                    <Select
                      name='companySelection'
                      labelId='companySelection'
                      id='companySelection'
                      value={values.companySelection || 'all'}
                      label={'Company Selection'}
                      onChange={(e) =>
                        handleCompanySelectionChange(e.target.value)
                      }
                    >
                      <MenuItem value='all'>All Companies</MenuItem>
                      <MenuItem value='specific'>Specific Company</MenuItem>
                    </Select>
                    {Boolean(
                      touched.companySelection && errors.companySelection
                    ) && (
                      <FormHelperText>
                        {errors?.companySelection}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Specific Company Selection - Multiple */}
                {values.companySelection === 'specific' && (
                  <Grid
                    item
                    xs={12}
                    sm={12}
                  >
                    <Autocomplete
                      multiple
                      options={companies}
                      getOptionLabel={(option) => option.name || ''}
                      value={
                        companies?.filter((company) =>
                          values.companyIds?.includes(company.id)
                        ) || []
                      }
                      onChange={(event, newValue) => {
                        const companyIds = newValue.map((company) => company.id);
                        setFieldValue('companyIds', companyIds);
                      }}
                      onInputChange={(event, newInputValue) => {
                        if (newInputValue) {
                          searchCompanies({
                            target: { value: newInputValue },
                          } as React.ChangeEvent<HTMLInputElement>);
                        }
                      }}
                      loading={isLoadingCompanies}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Select Companies'
                          error={Boolean(errors.companyIds && touched.companyIds)}
                          helperText={
                            Boolean(errors.companyIds && touched.companyIds)
                              ? errors?.companyIds
                              : 'Select one or more companies'
                          }
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.name}
                            {...getTagProps({ index })}
                            key={option.id}
                          />
                        ))
                      }
                      filterOptions={(options) => options}
                      ListboxProps={{
                        onScroll: (e) => onScrollFetchCompanies(e as any, ''),
                      }}
                    />
                  </Grid>
                )}
              </>
            )}

            {/* Broker Selection - only for Broker type (not Individual + Broker) */}
            {isBroker && (
              <>
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <FormControl
                    fullWidth
                    error={Boolean(
                      touched?.brokerSelection && errors?.brokerSelection
                    )}
                  >
                    <InputLabel>{'Broker Selection'}</InputLabel>
                    <Select
                      name='brokerSelection'
                      labelId='brokerSelection'
                      id='brokerSelection'
                      value={values.brokerSelection || 'all'}
                      label={'Broker Selection'}
                      onChange={(e) =>
                        handleBrokerSelectionChange(e.target.value)
                      }
                    >
                      <MenuItem value='all'>All Brokers</MenuItem>
                      <MenuItem value='specific'>Specific Brokers</MenuItem>
                    </Select>
                    {Boolean(
                      touched.brokerSelection && errors.brokerSelection
                    ) && (
                      <FormHelperText>
                        {errors?.brokerSelection}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Specific Broker Selection - Multiple */}
                {values.brokerSelection === 'specific' && (
                  <Grid
                    item
                    xs={12}
                    sm={12}
                  >
                    <Autocomplete
                      multiple
                      options={brokers}
                      getOptionLabel={(option) => option.name || ''}
                      value={
                        brokers?.filter((broker) =>
                          values.brokerIds?.includes(broker.id)
                        ) || []
                      }
                      onChange={(event, newValue) => {
                        const brokerIds = newValue.map((broker) => broker.id);
                        setFieldValue('brokerIds', brokerIds);
                      }}
                      onInputChange={(event, newInputValue) => {
                        if (newInputValue) {
                          searchBrokers({
                            target: { value: newInputValue },
                          } as React.ChangeEvent<HTMLInputElement>);
                        }
                      }}
                      loading={isLoadingBrokers}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Select Brokers'
                          error={Boolean(errors.brokerIds && touched.brokerIds)}
                          helperText={
                            Boolean(errors.brokerIds && touched.brokerIds)
                              ? errors?.brokerIds
                              : 'Select one or more brokers'
                          }
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.name}
                            {...getTagProps({ index })}
                            key={option.id}
                          />
                        ))
                      }
                      filterOptions={(options) => options}
                      ListboxProps={{
                        onScroll: (e) => onScrollFetchBrokers(e as any, ''),
                      }}
                    />
                  </Grid>
                )}
              </>
            )}

            <Grid
              item
              xs={12}
              sm={12}
            >
              <FormControl
                fullWidth
                error={Boolean(touched?.type && errors?.type)}
              >
                <InputLabel>{'Select Type'}</InputLabel>
                <Select
                  name='type'
                  labelId='type'
                  id='type'
                  value={values.type}
                  label={'Select Type'}
                  onChange={(e) => setFieldValue('type', e.target.value)}
                  MenuProps={{
                    style: {
                      maxHeight: 400,
                    },
                  }}
                >
                  {ADVERTISE_PACKAGE_TYPE?.map((type) => (
                    <MenuItem
                      key={type.value}
                      value={type.value}
                    >
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
                {Boolean(touched.type && errors.type) && (
                  <FormHelperText>{errors?.type}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid
              item
              xs={12}
              sm={12}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    color='primary'
                    name='activeState'
                    onChange={(e) =>
                      setFieldValue('activeState', e.target.checked)
                    }
                    checked={values.activeState}
                  />
                }
                label={'Active Status'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              resetForm();
              onClose();
            }}
            variant='outlined'
          >
            {'Cancel'}
          </Button>
          <LoadingButton
            type='submit'
            loading={isSubmitting}
            variant='contained'
          >
            {'Save'}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AdvertisePackageFormModal;
